import { AuthGuard } from '@/components/AuthGuard';
import ErrorBoundary from "@/components/ErrorBoundary";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Toaster } from '@/components/ui/toaster';
import { setNavigateFunction } from '@/utils/navigation.utils';
import * as Sentry from "@sentry/react";
import { Suspense, lazy, useEffect } from "react";
import { Navigate, Route, BrowserRouter as Router, Routes, useNavigate } from "react-router-dom";

// Lazy load all page components
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Orders = lazy(() => import("@/pages/Orders"));
const LiveOrders = lazy(() => import("@/pages/LiveOrdersSocket"));
const ViewOrder = lazy(() => import("@/pages/ViewOrder"));
const Stores = lazy(() => import("@/pages/Stores"));
const Products = lazy(() => import("@/pages/Products"));
const Users = lazy(() => import("@/pages/Users"));
const UserDetails = lazy(() => import("@/pages/UserDetails"));
const EditUser = lazy(() => import("@/pages/EditUser"));
const Drivers = lazy(() => import("@/pages/Drivers"));
const Promotions = lazy(() => import("@/pages/DeliveryFeePromotions"));
const CreatePromotion = lazy(() => import("@/pages/CreateDeliveryFeePromotion"));
const EditPromotion = lazy(() => import("@/pages/EditDeliveryFeePromotion"));
const Transactions = lazy(() => import("@/pages/Transactions"));
const WalletTransactions = lazy(() => import("@/pages/WalletTransactions"));
const Reports = lazy(() => import("@/pages/Reports"));
const Settings = lazy(() => import("@/pages/Settings"));
const CreateUser = lazy(() => import('./pages/CreateUser'));
const Categories = lazy(() => import('./pages/Categories'));
const FeaturedProducts = lazy(() => import('./pages/FeaturedProducts'));
const FeaturedVendors = lazy(() => import('./pages/FeaturedVendors'));
const CreateStore = lazy(() => import('./pages/CreateStore'));
const EditStore = lazy(() => import('./pages/EditStore'));
const OperationalAreas = lazy(() => import('./pages/OperationalAreas'));
const CreateOperationalArea = lazy(() => import('./pages/CreateOperationalArea'));
const EditOperationalArea = lazy(() => import('./pages/EditOperationalArea'));
const AssignVendors = lazy(() => import('./pages/AssignVendors'));
const OperationalAreaDetails = lazy(() => import('./pages/OperationalAreaDetails'));
// const Categories = lazy(() => import('./pages/Categories')); // Removed duplicate
const CreateCategory = lazy(() => import('./pages/CreateCategory'));
const EditCategory = lazy(() => import('./pages/EditCategory'));
const CreateProduct = lazy(() => import('./pages/CreateProduct'));
const EditProduct = lazy(() => import('./pages/EditProduct'));
// const Products = lazy(() => import('./pages/Products')); // Removed duplicate
const MobileHome = lazy(() => import('./pages/MobileHome'));
const Login = lazy(() => import('@/pages/Login'));
const AppCategories = lazy(() => import('./pages/AppCategories'));
const Translations = lazy(() => import('./pages/Translations'));
const DisplayCategories = lazy(() => import('./pages/DisplayCategories'));
const Reels = lazy(() => import('./pages/Reels'));
// const FeaturedVendors = lazy(() => import('./pages/FeaturedVendors')); // Removed duplicate
const AddFeaturedVendor = lazy(() => import('./pages/AddFeaturedVendor'));
const EditFeaturedVendor = lazy(() => import('./pages/EditFeaturedVendor'));
// const FeaturedProducts = lazy(() => import('./pages/FeaturedProducts')); // Removed duplicate
const CreateFeaturedProduct = lazy(() => import('./pages/CreateFeaturedProduct'));
const EditFeaturedProduct = lazy(() => import('./pages/EditFeaturedProduct'));
const EagleView = lazy(() => import('./pages/EagleView'));
const StorePayouts = lazy(() => import('./pages/StorePayouts'));
const StoreBalance = lazy(() => import('./pages/StoreBalance'));
const StoreItems = lazy(() => import('./pages/StoreItems'));
const ViewStore = lazy(() => import('./pages/ViewStore'));
const PushNotifications = lazy(() => import('./pages/PushNotifications'));
const CollectionsLog = lazy(() => import('./pages/CollectionsLog'));
const ProductReorder = lazy(() => import('./pages/ProductReorder'));
const OtherStores = lazy(() => import('./pages/OtherStores'));
const AddOtherStore = lazy(() => import('./pages/AddOtherStore'));
const EditOtherStore = lazy(() => import('./pages/EditOtherStore'));
const BestProducts = lazy(() => import('./pages/BestProducts'));
const CreateBestProduct = lazy(() => import('./pages/CreateBestProduct'));
const EditBestProduct = lazy(() => import('./pages/EditBestProduct'));
const CodBans = lazy(() => import('./pages/CodBans'));
const VendorRatings = lazy(() => import('./pages/VendorRatings'));
const StoreOrders = lazy(() => import('./pages/StoreOrders'));
const PromoCodes = lazy(() => import('./pages/PromoCodes'));
const CreatePromoCode = lazy(() => import('./pages/CreatePromoCode'));
const EditPromoCode = lazy(() => import('./pages/EditPromoCode'));
const PromoCodeDetails = lazy(() => import('./pages/PromoCodeDetails'));

// Loading component for Suspense fallback
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[400px]">
    <div className="text-muted-foreground">Loading...</div>
  </div>
);

function AppContent() {
  const navigate = useNavigate();

  useEffect(() => {
    setNavigateFunction(navigate);
  }, [navigate]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a');
      if (!anchor) return;

      const href = anchor.getAttribute('href');
      if (!href || href === '#' || href.startsWith('#') || anchor.hasAttribute('download')) return;

      const isExternal = href.startsWith('http://') || href.startsWith('https://') || href.startsWith('mailto:') || href.startsWith('tel:');

      if (isExternal) {
        e.preventDefault();
        e.stopPropagation();
        window.open(href, '_blank', 'noopener,noreferrer');
      }
    }

    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, []);

  const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);

  return (
    <Suspense fallback={<PageLoader />}>
      <SentryRoutes>
        <Route path="/login" element={<Login />} />
        <Route path="/" element={<Navigate to="/mobile" replace />} />
        <Route path="/" element={<AuthGuard><DashboardLayout /></AuthGuard>}>
          <Route index element={<Dashboard />} />
          <Route path="stores" element={<Stores />} />
          <Route path="stores/:vendorId" element={<ViewStore />} />
          <Route path="stores/:vendorId/items" element={<StoreItems />} />
          <Route path="stores/:vendorId/ratings" element={<VendorRatings />} />
          <Route path="stores/:vendorId/orders" element={<StoreOrders />} />
          <Route path="stores/create" element={<CreateStore />} />
          <Route path="stores/:id/edit" element={<EditStore />} />
          <Route path="items" element={<Products />} />
          <Route path="items/create" element={<CreateProduct />} />
          <Route path="/items/:id/edit" element={<EditProduct />} />
          <Route path="users" element={<Users />} />
          <Route path="users/:id" element={<UserDetails />} />
          <Route path="users/:id/edit" element={<EditUser />} />
          <Route path="users/create" element={<CreateUser />} />
          <Route path="drivers" element={<Drivers />} />
          <Route path="collections-log" element={<CollectionsLog />} />
          <Route path="orders" element={<Orders />} />
          <Route path="orders/:id" element={<ViewOrder />} />
          <Route path="live-orders" element={<LiveOrders />} />
          <Route path="eagle-view" element={<EagleView />} />
          <Route path="delivery-fee-promotions" element={<Promotions />} />
          <Route path="delivery-fee-promotions/create" element={<CreatePromotion />} />
          <Route path="delivery-fee-promotions/:id/edit" element={<EditPromotion />} />
          {/* Legacy routes - keep for backward compat */}
          <Route path="promotions" element={<Promotions />} />
          <Route path="promotions/create" element={<CreatePromotion />} />
          <Route path="promotions/:id/edit" element={<EditPromotion />} />
          <Route path="push-notifications" element={<PushNotifications />} />
          <Route path="transactions" element={<Transactions />} />
          <Route path="wallet-transactions" element={<WalletTransactions />} />
          <Route path="reports" element={<Reports />} />
          <Route path="settings" element={<Settings />} />
          <Route path="operational-areas" element={<OperationalAreas />} />
          <Route path="operational-areas/:id" element={<OperationalAreaDetails />} />
          <Route path="operational-areas/create" element={<CreateOperationalArea />} />
          <Route path="operational-areas/:id/edit" element={<EditOperationalArea />} />
          <Route path="operational-areas/:id/assign-vendors" element={<AssignVendors />} />
          <Route path="categories" element={<Categories />} />
          <Route path="categories/create" element={<CreateCategory />} />
          <Route path="categories/:id/edit" element={<EditCategory />} />
          <Route path="/mobile" element={<MobileHome />} />
          <Route path="/app-categories" element={<AppCategories />} />
          <Route path="/translations" element={<Translations />} />
          <Route path="/display-categories" element={<DisplayCategories />} />
          <Route path="/reels" element={<Reels />} />
          <Route path="/featured-vendors" element={<FeaturedVendors />} />
          <Route path="/featured-vendors/add" element={<AddFeaturedVendor />} />
          <Route path="/featured-vendors/:id/edit" element={<EditFeaturedVendor />} />
          <Route path="/featured-products" element={<FeaturedProducts />} />
          <Route path="/featured-products/create" element={<CreateFeaturedProduct />} />
          <Route path="/featured-products/:id/edit" element={<EditFeaturedProduct />} />
          <Route path="/best-products" element={<BestProducts />} />
          <Route path="/best-products/create" element={<CreateBestProduct />} />
          <Route path="/best-products/:id/edit" element={<EditBestProduct />} />
          <Route path="/items/reorder" element={<ProductReorder />} />
          <Route path="/store-balance" element={<StoreBalance />} />
          <Route path="/store-payouts" element={<StorePayouts />} />
          <Route path="/other-stores" element={<OtherStores />} />
          <Route path="/other-stores/add" element={<AddOtherStore />} />
          <Route path="/other-stores/:id/edit" element={<EditOtherStore />} />
          <Route path="cod-bans" element={<CodBans />} />
          <Route path="promo-codes" element={<PromoCodes />} />
          <Route path="promo-codes/create" element={<CreatePromoCode />} />
          <Route path="promo-codes/:id" element={<PromoCodeDetails />} />
          <Route path="promo-codes/:id/edit" element={<EditPromoCode />} />
        </Route>
      </SentryRoutes>
    </Suspense>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <AppContent />
        <Toaster />
      </Router>
    </ErrorBoundary>
  );
}

export default App;